import { Trans, useTranslation } from 'react-i18next';
import { Button, FormField, Input, Modal } from '@/components/ui';
import type { DeactivateAccountVM } from '../useDeactivateAccount';

/**
 * EAA-1820 (frame 86): title/copy/footer switch on the account's CURRENT
 * `enabled` — see `useDeactivateAccount` for why only the disable direction
 * demands typing the username to confirm.
 *
 * EAA-1804: footer swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`; the "type the username" field
 * wrapped in kit `FormField` + `Input`.
 */
export function DeactivateAccountDialog({ vm }: { vm: DeactivateAccountVM }) {
    const { t } = useTranslation();
    const row = vm.target;
    const open = Boolean(row);
    const disabling = Boolean(row?.enabled);

    return (
        // EAA-1804: root debugging handle, matching the folder's existing acc-* prefix.
        <Modal
            className="acc-deactivate-dialog"
            open={open}
            onCancel={vm.close}
            title={disabling ? t('console.accounts.deactivate.disableTitle') : t('console.accounts.deactivate.enableTitle')}
            footer={
                <>
                    <Button onClick={vm.close} disabled={vm.busy}>
                        {t('common.cancel')}
                    </Button>
                    {disabling ? (
                        <Button
                            danger
                            onClick={vm.submitDisable}
                            disabled={vm.busy || !vm.confirmMatches}
                        >
                            {vm.busy ? t('console.accounts.deactivate.disableBusy') : t('console.accounts.deactivate.disableButton')}
                        </Button>
                    ) : (
                        <Button type="primary" onClick={vm.submitEnable} disabled={vm.busy}>
                            {vm.busy ? t('console.accounts.deactivate.enableBusy') : t('console.accounts.deactivate.enableButton')}
                        </Button>
                    )}
                </>
            }
        >
            {disabling ? (
                <>
                    <div className="acc-deactivate-warning">
                        <strong>{t('console.accounts.deactivate.warnBold')}</strong>{' '}
                        {t('console.accounts.deactivate.warnBody')}
                    </div>
                    <p className="portal-admin-hint">{t('console.accounts.deactivate.disableHint')}</p>
                    <FormField
                        className="sa-dialog-field"
                        label={
                            <Trans
                                i18nKey="console.accounts.deactivate.confirmLabel"
                                values={{ username: row?.username }}
                                components={{ code: <code /> }}
                            />
                        }
                    >
                        <Input
                            value={vm.confirmText}
                            onChange={(e) => vm.setConfirmText(e.target.value)}
                            placeholder={row?.username}
                            autoComplete="off"
                        />
                    </FormField>
                </>
            ) : (
                <p className="portal-admin-hint">
                    {t('console.accounts.deactivate.enableHint', { username: row?.username })}
                </p>
            )}
            {vm.error && <p className="portal-admin-error">{vm.error}</p>}
        </Modal>
    );
}

export default DeactivateAccountDialog;
